import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home, Briefcase, Tag, Headset, Menu, Phone, Mail, Sparkles, FileText, Info, LogIn, LogOut, ChevronRight,
} from 'lucide-react';
import { Drawer, DrawerContent } from '../ui/drawer';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';

import { API } from '../../config';

/** Fixed 5-item bottom navigation, shown ONLY on mobile (md:hidden). */
const MobileBottomNav = () => {
  const loc = useLocation();
  const nav = useNavigate();
  const settings = useSettings();
  const { user, logout } = useAuth();
  const g = settings?.general || {};
  const mods = settings?.modules || {};
  const phone = g.support_phone || '1800-123-4567';
  const email = g.support_email || 'help@trainpanda.com';

  const [moreOpen, setMoreOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [pages, setPages] = useState([]);
  useEffect(() => { axios.get(`${API}/pages`).then((r) => setPages(r.data?.pages || [])).catch(() => {}); }, []);

  const isActive = (to) => (to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(to));

  const items = [
    { label: 'Home', icon: Home, to: '/' },
    { label: 'My Trips', icon: Briefcase, to: '/my-orders' },
    { label: 'Offers', icon: Tag, to: '/offers' },
    { label: 'Contact', icon: Headset, action: () => setContactOpen(true) },
    { label: 'More', icon: Menu, action: () => setMoreOpen(true) },
  ];

  const go = (to) => { setMoreOpen(false); nav(to); };

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-gray-100 shadow-[0_-4px_20px_-8px_rgba(0,0,0,0.15)] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {items.map((it) => {
            const active = it.to && isActive(it.to);
            const cls = `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-bold transition-colors ${
              active ? 'text-tp-violet-deep' : 'text-gray-500'
            }`;
            return it.to ? (
              <Link key={it.label} to={it.to} data-testid={`bn-${it.label.toLowerCase().replace(' ', '-')}`} className={cls}>
                <it.icon size={20} /> {it.label}
              </Link>
            ) : (
              <button
                key={it.label}
                type="button"
                onClick={(e) => {
                  if (e?.currentTarget) e.currentTarget.blur();
                  if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
                    document.activeElement.blur();
                  }
                  it.action();
                }}
                data-testid={`bn-${it.label.toLowerCase()}`}
                className={cls}
              >
                <it.icon size={20} /> {it.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Contact Us */}
      <Drawer open={contactOpen} onOpenChange={setContactOpen} shouldScaleBackground={false}>
        <DrawerContent data-testid="contact-drawer">
          <div className="px-4 pb-8 pt-2 max-w-md mx-auto w-full">
            <h3 className="text-lg font-black text-tp-ink text-center mb-4">Get in touch</h3>
            <a href={`tel:${phone}`} className="flex items-center gap-3 bg-tp-violet-soft rounded-2xl p-4 mb-3" data-testid="contact-call">
              <div className="w-11 h-11 rounded-full bg-tp-violet text-white flex items-center justify-center"><Phone size={18} /></div>
              <div><div className="text-[11px] text-gray-500 font-semibold uppercase">Call us</div><div className="font-bold text-tp-ink">{phone}</div></div>
            </a>
            <a href={`mailto:${email}`} className="flex items-center gap-3 bg-tp-lime-soft rounded-2xl p-4" data-testid="contact-email">
              <div className="w-11 h-11 rounded-full bg-tp-lime-deep text-white flex items-center justify-center"><Mail size={18} /></div>
              <div><div className="text-[11px] text-gray-500 font-semibold uppercase">Email us</div><div className="font-bold text-tp-ink">{email}</div></div>
            </a>
          </div>
        </DrawerContent>
      </Drawer>

      {/* More */}
      <Drawer open={moreOpen} onOpenChange={setMoreOpen} shouldScaleBackground={false}>
        <DrawerContent data-testid="more-drawer">
          <div className="px-4 pb-8 pt-2 max-w-md mx-auto w-full">
            <h3 className="text-lg font-black text-tp-ink text-center mb-4">More</h3>
            <div className="space-y-1">
              {mods.stories !== false && (
                <MoreRow icon={Sparkles} label="Travel Stories" onClick={() => go('/stories')} testid="more-stories" />
              )}
              {pages.map((p) => (
                <MoreRow key={p.slug} icon={FileText} label={p.title} onClick={() => go(`/page/${p.slug}`)} testid={`more-page-${p.slug}`} />
              ))}
              <MoreRow icon={Info} label="About TrainPanda" onClick={() => go('/page/about')} testid="more-about" />
              {user ? (
                <MoreRow icon={LogOut} label="Logout" danger onClick={() => { setMoreOpen(false); logout(); }} testid="more-logout" />
              ) : (
                <MoreRow icon={LogIn} label="Login / Signup" onClick={() => go('/login')} testid="more-login" />
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

const MoreRow = ({ icon: Icon, label, onClick, danger, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-left font-semibold ${
      danger ? 'text-red-600 hover:bg-red-50' : 'text-tp-ink hover:bg-tp-violet-soft'
    }`}
  >
    <Icon size={18} className={danger ? 'text-red-500' : 'text-tp-violet'} />
    <span className="flex-1">{label}</span>
    <ChevronRight size={16} className="text-gray-300" />
  </button>
);

export default MobileBottomNav;
